import { FieldData, Store } from 'rc-field-form/lib/interface';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios';
import { useCallback, useState } from 'react';
import http from 'App/lib/http';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { FormInstance } from 'antd/lib/form';

export interface FormSubmitOptions {
  url: string;
  form: FormInstance;
  method?: Method;
  axiosConfig?: AxiosRequestConfig;
  onSuccess?: (response: AxiosResponse, data: Store) => void;
  onFailure?: (response: AxiosResponse, data: Store) => void;
  beforeSubmit?: (data: Store) => Store;
}

export interface FormSubmitInstance {
  onSubmit: (values: Store) => void;
  isSubmitting: boolean;
}

const useFormSubmit = ({
  url,
  method = 'POST',
  form,
  axiosConfig = {},
  onSuccess = () => {},
  onFailure = () => {},
  beforeSubmit = (data) => data,
}: FormSubmitOptions): FormSubmitInstance => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onSubmit = useCallback(async () => {
    const data = form.getFieldsValue();

    setIsSubmitting(true);

    try {
      const response = await http.request(
        merge({ method, url, data: beforeSubmit(data) }, axiosConfig)
      );

      setTimeout(() => onSuccess(response, data));
    } catch (error) {
      setIsSubmitting(false);
      form.resetFields();
      if (axios.isAxiosError(error)) {
        form.setFields(transformError(error, data));
        if (error.response) {
          onFailure(error.response, data);
        }
        return;
      }
      throw error;
    }

    setIsSubmitting(false);
  }, [url, method]);

  return { isSubmitting, onSubmit };
};

const transformError = (error: AxiosError, data: Store): FieldData[] => {
  const errors = get(error, 'response.data.errors', {});
  return Object.keys(data).map((field: string) => ({
    name: field,
    value: get(data, field),
    errors: get(errors, field, []),
  }));
};

export default useFormSubmit;
